import { createFileRoute } from '@tanstack/react-router'
import {
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  Divider,
} from '@mui/material'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import { useCoinBalance, useCoinTransactions } from '@/hooks/useCoins'
import type { CoinTransaction } from '@/hooks/useCoins'

export const Route = createFileRoute('/_authenticated/coins/')({
  component: CoinsPage,
})

const typeLabels: Record<string, string> = {
  award: 'Toegekend',
  redeem: 'Ingewisseld',
  correction: 'Correctie',
}

const typeColors: Record<string, 'success' | 'error' | 'default'> = {
  award: 'success',
  redeem: 'error',
  correction: 'default',
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function TransactionRow({ tx }: { tx: CoinTransaction }) {
  const isPositive = tx.amount > 0
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        py: 1.5,
        px: 2,
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" noWrap>
          {tx.description}
        </Typography>
        <Typography variant="caption" color="text.disabled">
          {formatDate(tx.created_at)}
        </Typography>
      </Box>
      <Chip
        label={typeLabels[tx.transaction_type] ?? tx.transaction_type}
        color={typeColors[tx.transaction_type] ?? 'default'}
        size="small"
        sx={{ minWidth: 90 }}
      />
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          minWidth: 64,
          textAlign: 'right',
          color: isPositive ? 'success.main' : 'error.main',
        }}
      >
        {isPositive ? '+' : ''}{tx.amount}
      </Typography>
    </Box>
  )
}

export function CoinsPage() {
  const { balance, loading: balanceLoading, error: balanceError } = useCoinBalance()
  const { transactions, loading: txLoading, error: txError } = useCoinTransactions()

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Mijn coins
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Hier zie je jouw i4talent coin saldo en alle transacties.
      </Typography>

      {/* Balance card */}
      {balanceError ? (
        <Alert severity="error" sx={{ mb: 3 }}>{balanceError}</Alert>
      ) : (
        <Card sx={{ mb: 4, maxWidth: { xs: '100%', sm: 320 } }}>
          <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <MonetizationOnIcon sx={{ fontSize: 48, color: 'secondary.main' }} />
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Huidig saldo
              </Typography>
              {balanceLoading ? (
                <CircularProgress size={24} />
              ) : (
                <Typography variant="h3" sx={{ fontWeight: 700, color: 'secondary.main' }}>
                  {balance?.total_coins ?? 0}
                </Typography>
              )}
              <Typography variant="caption" color="text.disabled">
                coins
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Transaction list */}
      <Typography variant="h6" gutterBottom>
        Transacties
      </Typography>

      {txError && <Alert severity="error">{txError}</Alert>}

      {txLoading ? (
        <CircularProgress />
      ) : transactions.length === 0 ? (
        <Typography color="text.secondary">
          Nog geen transacties gevonden.
        </Typography>
      ) : (
        <Card>
          {transactions.map((tx, i) => (
            <Box key={tx.uuid}>
              <TransactionRow tx={tx} />
              {i < transactions.length - 1 && <Divider />}
            </Box>
          ))}
        </Card>
      )}
    </Box>
  )
}
